/**
 * Integrytas Group - Common Security Module (CSM)
 * Filtered Modal Single Select.
 *
 * Single-value sibling of FilteredModalMultiSelect. Per Integrytas standard:
 *  - Replaces native HTML <select> dropdowns.
 *  - Modal opens on trigger click.
 *  - If options.length > searchThreshold (default 10), an internal search
 *    bar is rendered to filter selection.
 *  - Optional per-field FACETS (§17.B5): pass `facets` plus `facet_values` on
 *    each option and the picker offers the same field filtering its backing
 *    list page does, not just free-text search. Inert when omitted.
 *  - Selection always keys on `internal_code`; display strings are
 *    pre-translated from *_tl tables.
 *  - A single click on an option commits it via onChange and closes the
 *    picker immediately — there is no separate Apply step. Single-select is
 *    a one-shot choice (unlike FilteredModalMultiSelect, which stages several
 *    picks behind an explicit Apply), so a click already fully expresses
 *    intent. The × control (or Escape) closes without changing anything.
 *
 * TYPEAHEAD (§11): the picker is fully keyboard-drivable, so a user need not
 * open the whole list and scroll. Above the search threshold the auto-focused
 * search box narrows the list; below it, typing a prefix jumps the highlight to
 * the first option that starts with what was typed (type-to-jump). In both
 * cases ArrowUp/ArrowDown move the highlight, Home/End jump to the ends, and
 * Enter accepts the highlighted option (commits via onChange and closes).
 */

import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { MultiSelectOption, SelectFacetDef } from './FilteredModalMultiSelect';
import { FacetFilter } from './FacetFilter';

export interface FilteredModalSingleSelectProps {
  /** Field heading above the trigger. Omit to render no heading — the trigger
   * then carries `placeholder` as its own watermark, so the control lines up
   * with adjacent unlabelled inputs. The modal still shows a title (see
   * `modalTitle`). */
  label?: string;
  options: MultiSelectOption[];
  selectedCode: string | null;
  onChange: (code: string | null) => void;
  placeholder?: string;
  /** Heading inside the picker modal. Falls back to `label`, then `placeholder`
   * — so a label-less control still opens a titled modal. */
  modalTitle?: string;
  disabled?: boolean;
  allowClear?: boolean;
  clearLabel?: string;
  searchThreshold?: number;
  /** Optional facets mirroring this object's list-page filter bar. Inert when omitted. */
  facets?: SelectFacetDef[];
}

/** How long a type-to-jump prefix persists before the next keystroke starts fresh. */
const TYPE_JUMP_RESET_MS = 700;

export function FilteredModalSingleSelect({
  label,
  options,
  selectedCode,
  onChange,
  placeholder,
  modalTitle,
  disabled = false,
  allowClear = true,
  clearLabel,
  searchThreshold = 10,
  facets,
}: FilteredModalSingleSelectProps) {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('common.select.placeholder', 'Select...');
  const resolvedClearLabel = clearLabel ?? t('common.select.none', '(none)');
  const resolvedModalTitle = modalTitle ?? label ?? resolvedPlaceholder;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [facetSelections, setFacetSelections] = useState<Record<string, string[]>>({});
  const [highlight, setHighlight] = useState(0);

  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const typeBuffer = useRef('');
  const typeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showSearch = options.length > searchThreshold;

  // Two stages, deliberately: the facet CHIPS are built from `searched`, so the
  // values on offer narrow with the text query, while `filtered` applies the
  // chosen facets on top. Building chips from `filtered` instead would let a
  // selection remove its own chip from the list.
  const searched = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter(o =>
      o.display_text.toLowerCase().includes(q) ||
      o.internal_code.toLowerCase().includes(q) ||
      (o.description ?? '').toLowerCase().includes(q),
    );
  }, [options, query]);

  const filtered = useMemo(() => {
    const activeFacetKeys = Object.keys(facetSelections).filter(k => facetSelections[k]?.length);
    if (activeFacetKeys.length === 0) return searched;
    return searched.filter(o => activeFacetKeys.every(k => facetSelections[k].includes(o.facet_values?.[k] ?? '')));
  }, [searched, facetSelections]);

  // Only active options are keyboard-navigable / selectable; inactive ones stay
  // visible but are skipped by the highlight, exactly as a click skips them.
  const selectable = useMemo(() => filtered.filter(o => o.is_active !== false), [filtered]);

  // The ordered list the highlight walks: an optional leading "clear" row
  // (represented as null) followed by every selectable option code.
  const navCodes = useMemo<(string | null)[]>(
    () => (allowClear ? [null, ...selectable.map(o => o.internal_code)] : selectable.map(o => o.internal_code)),
    [allowClear, selectable],
  );
  const navIndexByCode = useMemo(() => {
    const m = new Map<string, number>();
    navCodes.forEach((code, i) => { if (code !== null) m.set(code, i); });
    return m;
  }, [navCodes]);

  const facetOptionsFor = useCallback((key: string) => {
    const seen = new Map<string, number>();
    for (const o of searched) {
      const v = o.facet_values?.[key];
      if (v == null) continue;
      seen.set(v, (seen.get(v) ?? 0) + 1);
    }
    return Array.from(seen.entries()).map(([value, count]) => ({ value, label: value, count }));
  }, [searched]);

  const cancel = useCallback(() => setOpen(false), []);

  const openModal = () => {
    if (disabled) return;
    setQuery('');
    setFacetSelections({});
    setOpen(true);
  };

  const choose = useCallback((code: string | null) => { onChange(code); setOpen(false); }, [onChange]);

  // When the list changes (open, typing, faceting), park the highlight on a
  // sensible row: the first option that STARTS WITH the current query, else the
  // currently-selected option, else the top of the list.
  useEffect(() => {
    if (!open) return;
    const q = query.trim().toLowerCase();
    if (q) {
      const idx = selectable.findIndex(o => o.display_text.toLowerCase().startsWith(q));
      setHighlight(idx >= 0 ? (navIndexByCode.get(selectable[idx].internal_code) ?? 0) : 0);
      return;
    }
    if (selectedCode !== null) setHighlight(navIndexByCode.get(selectedCode) ?? 0);
    else setHighlight(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query, filtered]);

  // Keep the highlighted row in view as it moves.
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-nav-index="${highlight}"]`)?.scrollIntoView?.({ block: 'nearest' });
  }, [highlight]);

  // With no search box there is nothing to focus, so focus the panel itself so
  // its keydown handler (type-to-jump, arrows) receives keystrokes.
  useEffect(() => {
    if (open && !showSearch) panelRef.current?.focus();
  }, [open, showSearch]);

  // Intercept ESC in capture phase so it closes only this picker,
  // not the parent Modal whose handler runs in the bubble phase.
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        cancel();
      }
    };
    document.addEventListener('keydown', h, true);
    return () => document.removeEventListener('keydown', h, true);
  }, [open, cancel]);

  useEffect(() => () => { if (typeTimer.current) clearTimeout(typeTimer.current); }, []);

  const jumpToPrefix = useCallback((prefix: string) => {
    const p = prefix.toLowerCase();
    const match = selectable.find(o => o.display_text.toLowerCase().startsWith(p));
    if (match) setHighlight(navIndexByCode.get(match.internal_code) ?? 0);
  }, [selectable, navIndexByCode]);

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(navCodes.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(0, h - 1));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setHighlight(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setHighlight(navCodes.length - 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlight >= 0 && highlight < navCodes.length) choose(navCodes[highlight]);
    } else if (!showSearch && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      // Type-to-jump: only when there is no search box to type into.
      typeBuffer.current += e.key;
      if (typeTimer.current) clearTimeout(typeTimer.current);
      typeTimer.current = setTimeout(() => { typeBuffer.current = ''; }, TYPE_JUMP_RESET_MS);
      jumpToPrefix(typeBuffer.current);
    }
  };

  const selectedOption = options.find(o => o.internal_code === selectedCode);
  const triggerLabel = selectedOption?.display_text;

  return (
    <div className="w-full">
      {label && (
        <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">
          {label}
        </label>
      )}

      <button
        type="button"
        onClick={openModal}
        disabled={disabled}
        className={
          'flex h-8 w-full items-center justify-between rounded border border-csm-border bg-white px-2 text-left text-csm-md ' +
          (disabled ? 'cursor-not-allowed bg-csm-surface-alt text-csm-watermark' : 'hover:border-csm-watermark')
        }
      >
        {triggerLabel ? (
          <span className="truncate">{triggerLabel}</span>
        ) : (
          <span className="truncate text-csm-watermark">{resolvedPlaceholder}</span>
        )}
        <span aria-hidden className="ml-2 text-csm-watermark">v</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-4">
          <div
            ref={panelRef}
            tabIndex={-1}
            onKeyDown={onKeyDown}
            className="flex max-h-[90vh] w-full max-w-lg flex-col rounded border border-csm-border bg-white shadow-xl focus:outline-none"
          >
            <header className="flex shrink-0 items-center justify-between border-b border-csm-border px-3 py-2">
              <h2 className="text-csm-md font-semibold text-csm-ink">{resolvedModalTitle}</h2>
              <button
                type="button"
                onClick={cancel}
                aria-label={t('common.close', 'Close')}
                className="text-csm-muted hover:text-csm-ink"
              >x</button>
            </header>

            {showSearch && (
              <div className="shrink-0 border-b border-csm-border p-2">
                <input
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder={t('common.search', 'Search...')}
                  className="h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
                  autoFocus
                />
              </div>
            )}

            {facets && facets.length > 0 && (
              <div className="flex shrink-0 flex-wrap gap-1 border-b border-csm-border p-2">
                {facets.map(f => (
                  <FacetFilter
                    key={f.key}
                    label={f.label}
                    options={facetOptionsFor(f.key)}
                    selected={facetSelections[f.key] ?? []}
                    onChange={values => setFacetSelections(prev => ({ ...prev, [f.key]: values }))}
                  />
                ))}
              </div>
            )}

            <ul ref={listRef} role="listbox" className="min-h-0 flex-1 overflow-y-auto">
              {allowClear && (
                <li
                  role="option"
                  aria-selected={selectedCode === null}
                  data-nav-index={0}
                  className={
                    'flex cursor-pointer items-center gap-2 border-b border-csm-border-soft px-3 py-2 text-csm-md italic ' +
                    (highlight === 0 ? 'ring-2 ring-inset ring-sidebar ' : '') +
                    (selectedCode === null ? 'bg-csm-info-soft/60 text-csm-ink' : 'text-csm-muted hover:bg-csm-surface-alt')
                  }
                  onClick={() => choose(null)}
                >
                  <input type="radio" readOnly checked={selectedCode === null} className="h-4 w-4 accent-gray-700" />
                  {resolvedClearLabel}
                </li>
              )}
              {filtered.length === 0 && (
                <li className="px-3 py-3 text-csm-sm italic text-csm-watermark">{t('common.no_matches', 'No matches.')}</li>
              )}
              {filtered.map(opt => {
                const checked = selectedCode === opt.internal_code;
                const inactive = opt.is_active === false;
                const navIndex = navIndexByCode.get(opt.internal_code);
                const highlighted = navIndex !== undefined && navIndex === highlight;
                return (
                  <li
                    key={opt.internal_code}
                    role="option"
                    aria-selected={checked}
                    data-nav-index={navIndex}
                    className={
                      'flex cursor-pointer items-center gap-2 border-b border-csm-border-soft px-3 py-2 text-csm-md ' +
                      (highlighted ? 'ring-2 ring-inset ring-sidebar ' : '') +
                      (inactive ? 'text-csm-watermark' : (checked ? 'bg-csm-info-soft/60' : 'hover:bg-csm-surface-alt'))
                    }
                    onClick={() => !inactive && choose(opt.internal_code)}
                  >
                    <input
                      type="radio"
                      readOnly
                      checked={checked}
                      disabled={inactive}
                      className="h-4 w-4 accent-gray-700"
                    />
                    <div className="flex-1">
                      <div className="font-medium">{opt.display_text}</div>
                      {opt.description && (
                        <div className="text-csm-xs text-csm-muted">{opt.description}</div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
